/**
 * /trading/preview renders the live /trading sidebar (Elena, Oct 2). The page stays
 * outside the shell guard so signed-out visitors can use the demo; for them the
 * sidebar shows the client menu a client sees on live /trading, and any item that
 * needs an account hits the normal auth guard, which sends them to sign-in.
 * /journal stays exempt (reading page) and is not handled here.
 */
import type { PlanTier } from '@/lib/stripe/types'

export function isTradingPreviewPath(pathname: string | null | undefined): boolean {
  if (!pathname) return false
  return pathname === '/trading/preview' || pathname.startsWith('/trading/preview/')
}

/** Signed-out preview visitors get the client menu; signed-in users keep their own. */
export function clientMenuForPreview(
  pathname: string | null | undefined,
  isAuthenticated: boolean,
): boolean {
  return isTradingPreviewPath(pathname) && !isAuthenticated
}

/**
 * /trading and /trading/preview get the trading-desk sidebar: Night chrome, the
 * Watchlist with Bid/Ask and no Pulse block (QA parity, Oct 2). Width is the same on
 * every page (see SIDEBAR_EXPANDED_WIDTH_PX).
 */
export function isTradingDeskSidebarPath(pathname: string | null | undefined): boolean {
  return pathname === '/trading' || isTradingPreviewPath(pathname)
}

/**
 * Signed-out preview visitors have no plan (tier none: 1 Watch slot plus the Operator+
 * upsell). They see the 5-slot Watchlist a desk client sees on live /trading instead.
 * Signed-in users keep their own plan everywhere.
 */
export function previewWatchTier(
  pathname: string | null | undefined,
  isAuthenticated: boolean,
  tier: PlanTier | 'none',
): PlanTier | 'none' {
  return clientMenuForPreview(pathname, isAuthenticated) ? 'starter' : tier
}

/**
 * Live /trading opens with the rail collapsed (TradingPage collapses it at <= 1440px).
 * The preview opens collapsed when there is no saved sidebar setting; a saved setting
 * (navigation-store, read by useNavigationHydration) wins.
 */
export function previewSidebarStartsCollapsed(persisted: { mainSidebarCollapsed?: boolean }): boolean {
  return persisted.mainSidebarCollapsed === undefined
}

/** Docked sidebar width on every page that has the sidebar (Elena, Oct 2). */
export const SIDEBAR_EXPANDED_WIDTH_PX = 300
export const SIDEBAR_COLLAPSED_WIDTH_PX = 64

export function sidebarWidthFor(collapsed: boolean): string {
  return `${collapsed ? SIDEBAR_COLLAPSED_WIDTH_PX : SIDEBAR_EXPANDED_WIDTH_PX}px`
}

/** /trading and /trading/preview force the rail collapsed at this viewport width and below. */
export const TRADING_DESK_FORCED_COLLAPSE_MAX_WIDTH = 1440

export function tradingDeskForcesCollapse(
  pathname: string | null | undefined,
  viewportWidth: number,
): boolean {
  return isTradingDeskSidebarPath(pathname) && viewportWidth <= TRADING_DESK_FORCED_COLLAPSE_MAX_WIDTH
}

/**
 * The rail when nothing forces it: the user's saved setting. With nothing saved the
 * preview opens collapsed and every other page (live /trading included) expanded.
 */
export function unforcedSidebarCollapsed(
  persisted: { mainSidebarCollapsed?: boolean },
  pathname: string | null | undefined,
): boolean {
  return persisted.mainSidebarCollapsed ?? isTradingPreviewPath(pathname)
}

/**
 * The forced start (Elena, Oct 2, final): /trading and /trading/preview at <= 1440px, or
 * the preview with nothing saved. Forcing only sets the STARTING state; a manual toggle
 * then applies for that visit only and never writes mainSidebarCollapsed.
 */
export function tradingDeskForcedStart(
  pathname: string | null | undefined,
  viewportWidth: number,
  persisted: { mainSidebarCollapsed?: boolean },
): boolean {
  return (
    tradingDeskForcesCollapse(pathname, viewportWidth) ||
    (isTradingPreviewPath(pathname) && persisted.mainSidebarCollapsed === undefined)
  )
}

/** Rail state a desk route starts with: collapsed when forced, else the saved setting. */
export function tradingDeskStartsCollapsed(
  pathname: string | null | undefined,
  viewportWidth: number,
  persisted: { mainSidebarCollapsed?: boolean },
): boolean {
  return tradingDeskForcedStart(pathname, viewportWidth, persisted) || unforcedSidebarCollapsed(persisted, pathname)
}

/**
 * Rail state the Sidebar renders. Until the client has read the viewport and the saved
 * setting (server render and the hydration render), desk routes render collapsed (64px):
 * the server cannot know either, and every forced case is collapsed, so the first painted
 * width is never 300 when it should be 64. Other pages keep the store value (unchanged).
 */
/** Elena's click transition (0.12s), on every property that sizes the rail. */
export const SIDEBAR_CLICK_TRANSITION_MS = 120

/**
 * Width transition for the rail. Off ('none') until the post-load step has painted, so
 * the load-time 64 -> 300 grow (or 300 -> 64) is an instant step; on for later clicks.
 * width, min-width and max-width all change together, so all three transition.
 */
export function sidebarWidthTransition(enabled: boolean, reduceMotion: boolean | null): string {
  if (!enabled || reduceMotion) return 'none'
  const ease = `${SIDEBAR_CLICK_TRANSITION_MS}ms cubic-bezier(0.16, 1, 0.3, 1)`
  return `width ${ease}, min-width ${ease}, max-width ${ease}`
}

export function sidebarRenderCollapsed(
  pathname: string | null | undefined,
  storeCollapsed: boolean,
  clientReady: boolean,
): boolean {
  if (!clientReady && isTradingDeskSidebarPath(pathname)) return true
  return storeCollapsed
}
